import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Chip, Alert, Typography } from "@mui/material";
import { intl, DmsSelect } from "dmsbasereact";
import { useDmsAnalyticsContext } from "./DmsAnalyticsContext";
import { DEFAULT_PERIODS, DEFAULT_MONTHS, CURRENT_YEAR, DEFAULT_YEARS_RANGE, ALL_YEARS_VALUE, MAX_RECOMMENDED_YEARS } from "./constants";

const YEARS = Array.from({ length: DEFAULT_YEARS_RANGE + 2 }, (_, i) => CURRENT_YEAR - i);

/**
 * DmsAnalyticsPeriodPicker - Period selection component for analytics
 * Provides multi-year selection with chips and "All Time" option.
 * Shows a warning when more than MAX_RECOMMENDED_YEARS are selected.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.showMonthSelects - Show month range selectors
 * @param {boolean} props.showYearSelect - Show year selector
 * @param {Object} props.sx - MUI sx styling props
 */
export const DmsAnalyticsPeriodPicker = ({
    showMonthSelects = true,
    showYearSelect = true,
    sx,
    ...props
}) => {
    const { filters, updateFilter } = useDmsAnalyticsContext();

    const isActivePeriod = useCallback((period) => {
        return filters.startMonth === period.startMonth && filters.endMonth === period.endMonth;
    }, [filters.startMonth, filters.endMonth]);

    const handlePeriodClick = useCallback((period) => {
        updateFilter("startMonth", period.startMonth);
        updateFilter("endMonth", period.endMonth);
    }, [updateFilter]);

    const isAllYearsSelected = filters.years.length === 1 && filters.years[0] === ALL_YEARS_VALUE;
    const showWarning = isAllYearsSelected || filters.years.length > MAX_RECOMMENDED_YEARS;

    const isYearSelected = useCallback((year) => {
        return filters.years.includes(year);
    }, [filters.years]);

    const handleYearToggle = useCallback((year) => {
        const currentYears = filters.years;

        // If clicking "All Years"
        if (year === ALL_YEARS_VALUE) {
            if (isAllYearsSelected) {
                // Deselect all -> back to current year only
                updateFilter("years", [CURRENT_YEAR]);
            } else {
                // Select all years
                updateFilter("years", [ALL_YEARS_VALUE]);
            }
            return;
        }

        // If currently "All Years" is selected, switch to just this year
        if (isAllYearsSelected) {
            updateFilter("years", [year]);
            return;
        }

        // Toggle the specific year
        if (currentYears.includes(year)) {
            // Remove year - but keep at least one selected
            const newYears = currentYears.filter((y) => y !== year);
            if (newYears.length === 0) {
                updateFilter("years", [CURRENT_YEAR]);
            } else {
                updateFilter("years", newYears);
            }
        } else {
            // Add year
            updateFilter("years", [...currentYears, year].sort((a, b) => b - a));
        }
    }, [filters.years, isAllYearsSelected, updateFilter]);

    const monthOptions = useMemo(
        () =>
            DEFAULT_MONTHS.map((m) => ({
                value: m.value,
                label: intl.get(m.labelKey).d(m.fallback),
            })),
        []
    );

    return (
        <Box sx={{ ...sx }} {...props}>
            {showWarning && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {intl.get("DmsAnalytics.Warning.AllYears").d(
                        "Buscar dados de todo o período pode levar bastante tempo devido à quantidade de dados. Recomendamos filtrar por no máximo 5 anos."
                    )}
                </Alert>
            )}
            <Box sx={{ display: "flex", gap: 0.5, mb: showMonthSelects ? 1 : 0, flexWrap: "wrap" }}>
                {DEFAULT_PERIODS.map((period) => (
                    <Chip
                        key={period.key}
                        label={intl.get(period.labelKey).d(period.fallback)}
                        size="small"
                        onClick={() => handlePeriodClick(period)}
                        variant={isActivePeriod(period) ? "filled" : "outlined"}
                        color={isActivePeriod(period) ? "primary" : "default"}
                        sx={{ cursor: "pointer" }}
                    />
                ))}
            </Box>
            {showYearSelect && (
                <Box sx={{ mb: showMonthSelects ? 1 : 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                        {intl.get("DmsAnalytics.Filter.Years").d("Anos (clique para selecionar múltiplos)")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {YEARS.map((year) => (
                            <Chip
                                key={year}
                                label={String(year)}
                                size="small"
                                onClick={() => handleYearToggle(year)}
                                variant={isYearSelected(year) && !isAllYearsSelected ? "filled" : "outlined"}
                                color={isYearSelected(year) && !isAllYearsSelected ? "primary" : "default"}
                                sx={{ cursor: "pointer" }}
                            />
                        ))}
                        <Chip
                            key={ALL_YEARS_VALUE}
                            label={intl.get("DmsAnalytics.Filter.AllYears").d("Todo o Período")}
                            size="small"
                            onClick={() => handleYearToggle(ALL_YEARS_VALUE)}
                            variant={isAllYearsSelected ? "filled" : "outlined"}
                            color={isAllYearsSelected ? "warning" : "default"}
                            sx={{ cursor: "pointer" }}
                        />
                    </Box>
                </Box>
            )}
            {showMonthSelects && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <DmsSelect
                        label={intl.get("DmsAnalytics.Filter.StartMonth").d("De")}
                        value={filters.startMonth}
                        onChange={(value) => updateFilter("startMonth", Number(value))}
                        options={monthOptions}
                        size="small"
                        sx={{ minWidth: 100 }}
                    />
                    <DmsSelect
                        label={intl.get("DmsAnalytics.Filter.EndMonth").d("Até")}
                        value={filters.endMonth}
                        onChange={(value) => updateFilter("endMonth", Number(value))}
                        options={monthOptions}
                        size="small"
                        sx={{ minWidth: 100 }}
                    />
                </Box>
            )}
        </Box>
    );
};

DmsAnalyticsPeriodPicker.propTypes = {
    showMonthSelects: PropTypes.bool,
    showYearSelect: PropTypes.bool,
    sx: PropTypes.object,
};
